/**
 * Admin API Client
 *
 * Wraps `fetch` to automatically attach the Cognito access token on every request
 * and raise typed errors on non-2xx responses.
 *
 * Used by React Query hooks throughout the app. Example:
 *
 *   const { data } = useQuery({
 *     queryKey: ['tenants', searchQ],
 *     queryFn: () => api.get(`/tenants?q=${searchQ}`),
 *   });
 */

import { config } from './config';
import { getAccessToken } from './cognito';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await getAccessToken();
  if (!token) {
    throw new ApiError(401, 'unauthenticated', 'no active session');
  }

  const url = `${config.api.baseUrl}${path}`;
  const res = await fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 204) {
    return undefined as T;
  }

  const text = await res.text();
  const payload = text ? safeJson(text) : undefined;

  if (!res.ok) {
    const err = (payload as { error?: { code?: string; message?: string } } | undefined)?.error;
    throw new ApiError(
      res.status,
      err?.code ?? 'http-error',
      err?.message ?? `HTTP ${res.status}`,
      payload,
    );
  }

  return payload as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};
